import Dinero from 'dinero.js';
import type { MultiPolygon } from 'geojson';
import { cloneDeep, map, mergeWith, toArray } from 'lodash';
import set from 'lodash/set';
import { DateTime } from 'luxon';
import { nanoid } from 'nanoid';
import { proxy } from 'valtio';
import { getTransactions as apiGetTransactions, getDataRequestsV2 } from '~/api/projects2';
import { geomToMultiPolygon } from '~/common/geoUtils';
import { compareWithArrayCopy } from '~/common/storeUtils';
import {
  type CircleObject,
  defaultBasemapStyle,
  EditorMode,
  LoadingState,
  type MapDetail,
  type MapObject,
  type Project,
  ProjectGeometryType,
  RequestStatus,
  type TLoadedState,
  type TLoadingState,
  TransactionCategory,
  type UnitSystem,
} from '~/common/types';
import { MapStoreBase } from '~/components2/Map';
import { getCircleDestCoords } from '~/components2/Map/canvas/circle/common';
import { SmallProjectAreaLimit } from '~/pages/common/TopNav/common';

export const CHANGE_ORDER_FEE = Dinero({ amount: 30000 });

export interface Transaction {
  id: string;
  amount: Dinero.Dinero;
  description: string;
  category: string;
  createdBy: {
    id: string;
    name: string;
    email: string;
  };
  type: 'debit' | 'credit' | 'discount';
  createdAt: DateTime;
  updatedAt: DateTime;
}

const defaultCurrentMap: TLoadingState | (TLoadedState & MapDetail) = {
  isLoading: false,
  id: 'temp',
  name: 'temp',
  image: 'temp',
  unitSystem: 'imperial',
  baseMap: defaultBasemapStyle,
  createdAt: DateTime.now(),
  createdBy: {
    name: 'me',
  },
  updatedAt: DateTime.now(),
  updatedBy: {
    name: 'me',
  },
  objects: {},
};

function getIsProjectCostTransaction(t: Transaction) {
  return (
    t.category === TransactionCategory.EditProjectBoundary ||
    t.category === TransactionCategory.NewProject ||
    t.category === TransactionCategory.NewProjectLite ||
    t.category === TransactionCategory.NewProposal ||
    t.category === TransactionCategory.NewProjectPlus
  );
}

class EditGeometryStore extends MapStoreBase {
  inEditGeometryMode: boolean = false;
  project: TLoadingState | (TLoadedState & Project) = LoadingState;
  projectId: string = '';
  editorMode: EditorMode = EditorMode.None;
  currentMap: TLoadingState | (TLoadedState & MapDetail) = {
    ...LoadingState,
  };

  geometryType: ProjectGeometryType | undefined = undefined;
  initialGeometry?: GeoJSON.MultiPolygon | GeoJSON.MultiLineString = undefined;

  projectArea: number = 0;
  projectAreaLimit: number = 0;

  lineBuffer: number = 40;

  alertUserForMapRequests: boolean = false;
  transactions: Transaction[] = [];

  shouldAddChangeOrderFee = false;

  setAlertUserForMapRequests(alert: boolean) {
    this.alertUserForMapRequests = alert;
  }

  setIsEditingProject(isEditing: boolean) {
    if (isEditing === false) {
      this.currentMap = { ...defaultCurrentMap };
      (this.currentMap as MapDetail).objects = {};
      this.setEditorMode(EditorMode.None);
      this.resetCurrentObjectId();
    }

    this.inEditGeometryMode = isEditing;
  }

  reset() {
    this.transactions = [];
    this.initialGeometry = undefined;
    this.resetCurrentObjectId();
    this.project = { isLoading: true };
    this.currentMap = { isLoading: true };
    this.setEditorMode(EditorMode.None);
    this.inEditGeometryMode = false;
    this.shouldAddChangeOrderFee = false;
  }

  get currentObject() {
    if (!this.currentMap.isLoading && this.currentObjectId) {
      return this.currentMap.objects[this.currentObjectId];
    }
    return undefined;
  }

  get objectsAsArray(): MapObject[] {
    if (!this.currentMap.isLoading) {
      return toArray(this.currentMap.objects);
    } else {
      return [];
    }
  }

  get geometryAsMultiPolygon(): MultiPolygon | undefined {
    const objectsAsArray = this.objectsAsArray;
    if (objectsAsArray.length) {
      return geomToMultiPolygon({
        objectsAsArray,
        geometryType: this.geometryType,
      });
    }
    return undefined;
  }

  get objectsOnMainSurface() {
    if (!this.currentMap.isLoading) {
      if (this.currentObjectId) {
        const copy = { ...this.currentMap.objects };
        delete copy[this.currentObjectId];

        return copy;
      } else {
        return this.currentMap.objects;
      }
    } else {
      return {};
    }
  }

  get currentUnitSystem(): UnitSystem {
    return 'imperial';
  }

  setEditorMode(mode: EditorMode): void {
    if (mode === EditorMode.None) {
      this.currentObjectId = undefined;
    }

    this.editorMode = mode;
  }

  generateSortOrderKey(): string {
    return 'abc';
  }

  setCurrentObjectId(objectId: string): void {
    if (!this.currentMap.isLoading) {
      const object = this.currentMap.objects[objectId];
      if (object) {
        this.currentObjectId = object.id;
        if (object.type === 'polygon') {
          this.setEditorMode(EditorMode.EditPolygon);
        } else if (object.type === 'line') {
          this.setEditorMode(EditorMode.EditLine);
        } else if (object.type === 'circle') {
          this.setEditorMode(EditorMode.EditCircle);
        }
      }
    }
  }

  resetCurrentObjectId(): void {
    this.currentObjectId = undefined;
    this.setEditorMode(EditorMode.None);
  }

  async newObject(
    o: Omit<MapObject, 'createdBy' | 'updatedBy'>,
    setCurrentObject: boolean = true,
    map?: mapboxgl.Map | undefined,
  ): Promise<MapObject | undefined> {
    o.title = '';
    o.properties = {
      ...o.properties,
      color: 'orange',
    };

    if (o.type === 'line') {
      o.properties = {
        ...o.properties,
        buffer: this.lineBuffer,
      };
    }

    set(this.currentMap, `objects.${o.id}`, o);

    if (setCurrentObject) {
      this.setEditorMode(EditorMode.None);
      setTimeout(() => {
        this.setCurrentObjectId(o.id);
      }, 100);
    }
    return undefined;
  }

  async updateObject<T = MapObject>(o: Partial<T>): Promise<void> {
    if (!this.currentMap.isLoading && this.currentObjectId) {
      const merged = mergeWith(this.currentObject, o, compareWithArrayCopy);

      this.currentMap.objects[this.currentObjectId!] = merged;
    }
  }

  async deleteObject<T = MapObject>(o: T, shouldPushToUndoStack?: boolean): Promise<MapObject | undefined> {
    if (!this.currentMap.isLoading) {
      delete this.currentMap.objects[(o as MapObject).id];
    }

    return undefined;
  }

  updateLineBuffer(buffer: number) {
    this.lineBuffer = buffer;
  }

  async bootstrap(project: Project) {
    this.currentMap = { ...defaultCurrentMap };
    (this.currentMap as MapDetail).objects = {};
    this.initialGeometry = undefined;
    this.projectId = project.id || this.projectId;
    // create deep copy so as not to affect the original project passed in.
    this.project = { ...cloneDeep(project), isLoading: false };

    const activeDataRequests = (await getDataRequestsV2(project.id)).filter(
      (r) => r.requestStatus !== RequestStatus.Pending,
    );
    if (activeDataRequests.length > 0) {
      this.shouldAddChangeOrderFee = true;
    }

    if (project.billingTier !== 'draft') {
      const transactions = await apiGetTransactions<Transaction[]>(this.projectId);

      if (transactions) {
        this.transactions = transactions.filter(getIsProjectCostTransaction);
      }
    }

    // @ts-expect-error extra error check
    if (this.project.geometry.type === 'Polygon') {
      throw new Error('Invalid geometry type, should be MultiPolygon');
    }

    // filter project data
    if (this.project.geometryType === ProjectGeometryType.Linear && this.project.linear?.buffer) {
      this.lineBuffer = this.project.linear.buffer.offset;
      this.geometryType = ProjectGeometryType.Linear;
      this.initialGeometry = {
        ...cloneDeep(this.project.linear.geometry as GeoJSON.MultiLineString),
      };
    } else if (this.project.geometryType === ProjectGeometryType.Small_3ac) {
      this.geometryType = ProjectGeometryType.Small_3ac;
      this.projectAreaLimit = SmallProjectAreaLimit;
      this.initialGeometry = { ...cloneDeep(this.project.geometry) };
    } else if (this.project.geometryType === ProjectGeometryType.Circle && !!this.project.circle?.radius) {
      this.geometryType = ProjectGeometryType.Circle;
      const radius = this.project.circle.radius.value; // in feet
      const ml: GeoJSON.MultiLineString = {
        type: 'MultiLineString',
        coordinates: map(this.project!.circle.geometry.coordinates, (p) => {
          const destCoords = getCircleDestCoords({
            lngLat: { lng: p[0], lat: p[1] },
            radius,
          }).geometry.coordinates;
          return [p, destCoords];
        }),
      };

      // initial geometry needs to be a mocked lineString for cost/dimension calcs
      this.initialGeometry = { ...ml };
    } else {
      this.geometryType = ProjectGeometryType.Polygon;
      this.initialGeometry = { ...cloneDeep(this.project.geometry) };
    }

    if (!this.project.isLoading) {
      if (this.geometryType === ProjectGeometryType.Circle) {
        const geometry = this.project.circle!.geometry as GeoJSON.MultiPoint;
        const radius = this.project.circle!.radius.value; // in feet
        geometry.coordinates.forEach((position) => {
          const c: Omit<CircleObject, 'createdBy' | 'updatedBy'> = {
            id: nanoid(),
            title: '',
            description: '',
            type: 'circle',
            sortOrder: this.generateSortOrderKey(),
            geometry: {
              type: 'Point',
              coordinates: position,
            },
            properties: {
              radius,
              color: 'orange',
              strokeOpacity: 1,
              fillOpacity: 0.25,
              strokeWidth: 4,
              strokeStyle: 'solid',
              showArea: true,
              showRadius: true,
              showTitle: false,
              labelAlwaysVisible: false,
              useMilesToDisplayRadius: true,
            },
          };

          this.newObject(c, false);
        });
      } else if (this.geometryType === ProjectGeometryType.Linear) {
        const geometry = this.project.linear!.geometry as GeoJSON.MultiLineString;

        geometry.coordinates.forEach((lineString) => {
          const l: Omit<MapObject, 'createdBy' | 'updatedBy'> = {
            id: nanoid(),
            title: '',
            description: '',
            type: 'line',
            sortOrder: this.generateSortOrderKey(),
            geometry: {
              type: 'LineString',
              coordinates: lineString,
            },
            properties: {
              color: 'orange',
              strokeOpacity: 1,
              strokeWidth: 4,
              strokeStyle: 'solid',
              showLength: true,
              showTitle: false,
              labelAlwaysVisible: false,
            },
          };

          this.newObject(l, false);
        });
      } else {
        const geometry = this.project.geometry as GeoJSON.MultiPolygon;

        geometry.coordinates.forEach((polygon) => {
          const p: Omit<MapObject, 'createdBy' | 'updatedBy'> = {
            id: nanoid(),
            title: '',
            description: '',
            type: 'polygon',
            sortOrder: this.generateSortOrderKey(),
            geometry: {
              type: 'Polygon',
              coordinates: polygon,
            },
            properties: {
              fillOpacity: 0.25,
              strokeOpacity: 1,
              strokeWidth: 4,
              strokeStyle: 'solid',
              showArea: true,
              showTitle: false,
              labelAlwaysVisible: false,
            },

            ...(this.geometryType === ProjectGeometryType.Circle && {
              interactive: {
                lines: false,
                vertices: false,
              },
            }),
          };

          this.newObject(p, false);
        });
      }
    }
  }
}

const editGeometryStore = proxy(new EditGeometryStore());
export default editGeometryStore;
